import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  destroy,
  getFormSyncErrors,
  hasSubmitSucceeded,
  isSubmitting,
  isValid,
  submit
} from 'redux-form';
import { useTypedSelector } from '@cube3/state/src/redux/components/Hooks/useTypedSelector';
import { Asset } from '@cube3/common/model/schema/resources/asset';

export const generateBroadcastFormId = (assetId: string) => {
  return `tv_broadcast_data_${assetId}`;
};

export const useBroadcastMetadataForms = (assets: Asset[]) => {
  const dispatch = useDispatch();

  const forms = useMemo(() => {
    return assets.map((asset) => ({
      id: asset.id,
      form: generateBroadcastFormId(asset.id)
    }));
  }, [assets]);

  const allFormValues = useTypedSelector((state) => {
    const form = state.form;
    const val = forms.map((item) => {
      const values = form[item.form]?.values;
      if (values) {
        return {
          parameters: {
            broadcast: {
              title: values.title,
              advertiser: values.client,
              product_service: values.product_service,
              commercial_length: values.length,
              version: values.version,
              export_filename: values.export_filename,
              tagon: values.tagon,
              omruil: values.omruil,
              low_loudness: values.low_loudness,
              commercial_type: values.commercial_type
            }
          },
          asset_id: item.id
        };
      }
      return null;
    });
    return val.filter(Boolean);
  });

  const allFormsValid = useSelector((state) => {
    return forms.reduce(
      (acc, form) => {
        const valid = isValid(form.form)(state);

        return {
          ...acc,
          validForms: valid ? [...acc.validForms, form.id] : acc.validForms,
          allValid: !valid ? false : acc.allValid
        };
      },
      { validForms: [], allValid: true }
    );
  });

  const formSubmitting = useSelector((state) => {
    return forms.reduce(
      (acc, form) => {
        const submitting = isSubmitting(form.form)(state);

        return {
          ...acc,
          [form.id]: submitting,
          anySubmitting: acc.anySubmitting ? true : submitting
        };
      },
      { anySubmitting: false }
    );
  });

  const { allSucceeded: allFormsSucceeded, anySucceeded: anyFormsSucceeded } =
    useSelector((state) => {
      return forms.reduce(
        (acc, form) => {
          const success = hasSubmitSucceeded(form.form)(state);

          return {
            ...acc,
            [form.id]: success,
            anySucceeded: acc.anySucceeded ? true : success,
            allSucceeded: !success ? false : acc.allSucceeded
          };
        },
        { allSucceeded: true, anySucceeded: false }
      );
    });

  const allFormErrors = useSelector((state) => {
    return forms.reduce(
      (acc, form) => {
        const errors = getFormSyncErrors(form.form)(state);
        return {
          ...acc,
          errors: { ...acc.errors, [form.id]: errors },
          hasErrors: Object.keys(errors).length ? true : acc.hasErrors
        };
      },
      { hasErrors: false, errors: {} }
    );
  });

  /** submit all forms except the first export, because it has been created seperately */
  const submitAllForms = useCallback(() => {
    forms.forEach((asset) => {
      if (allFormsValid.allValid) {
        dispatch(submit(asset.form));
      }
    });
  }, [forms, allFormsValid.allValid, dispatch]);

  /** Maps over the retrieved forms and dispatches a destroy event per form */
  const destroyAllForms = useCallback(() => {
    forms.forEach((form) => {
      requestAnimationFrame(() => {
        dispatch(destroy(form.form));
      });
    });
  }, [dispatch, forms]);

  return {
    allFormValues,
    allFormsValid,
    submitAllForms,
    allFormsSucceeded: allFormsSucceeded && anyFormsSucceeded,
    formSubmitting: formSubmitting.anySubmitting,
    destroyAllForms,
    allFormErrors
  };
};
